/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { UseLazyQuery /*, UseQueryStateOptions*/ } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { QueryDefinition } from '@reduxjs/toolkit/query';

const SCROLL_POSITION_GAP = 400;

type InfinityListArgs = Partial<Record<string, unknown>>;

type ListResponse<DataItem> = DataItem[];
type ResponseWithDataProp<DataItem> = { data: ListResponse<DataItem>; total_count: number };

type LazyQueryResponse<DataItem> = ResponseWithDataProp<DataItem> | ListResponse<DataItem>;

type UseInfinityParams<DataItem, Args extends InfinityListArgs> = {
    useLazyQuery: UseLazyQuery<QueryDefinition<Args, any, any, LazyQueryResponse<DataItem>, any>>;
    args: { limit?: number } & Args;
    getResponseItems?: (listItem: DataItem) => Partial<Args>;
    getPaginationParams: (listItem: DataItem) => Partial<Args>;
    skip?: boolean;
    // options?: UseQueryStateOptions<QueryDefinition<Args, any, any, Data[], any>, Record<string, any>>;
};

export const useInfiniteScroll = <DataItem, Args extends InfinityListArgs>({
    useLazyQuery,
    getPaginationParams,
    // options,
    args,
    skip,
}: UseInfinityParams<DataItem, Args>) => {
    const [data, setData] = useState<ListResponse<DataItem>>([]);
    const [totalCount, setTotalCount] = useState<number | void>();
    const scrollElement = useRef<HTMLElement>(document.documentElement);
    const isLoadingRef = useRef<boolean>(false);
    const isDisabledMoreRef = useRef<boolean>(false);
    const lastRequestParams = useRef<Args | undefined>(undefined);
    const { limit, ...argsProp } = args;
    const lastArgsProps = useRef<Partial<Args>>(null);

    const [getItems, { isLoading, isFetching }] = useLazyQuery({ ...args } as Args);

    const getDataRequest = (params: Args) => {
        const request = getItems({
            limit,
            ...params,
        } as Args).unwrap();

        request.then(() => {
            lastRequestParams.current = { ...params };
        });

        return request;
    };

    const getEmptyList = () => {
        isLoadingRef.current = true;

        setData([]);

        getDataRequest(argsProp as Args).then((result: LazyQueryResponse<DataItem>) => {
            // setDisabledMore(false);
            isDisabledMoreRef.current = false;

            if ('data' in result) {
                setData(result.data as ListResponse<DataItem>);
                setTotalCount(result.total_count);
            } else {
                setData(result as ListResponse<DataItem>);
                setTotalCount();
            }

            isLoadingRef.current = false;
        });
    };

    useEffect(() => {
        if (!isEqual(argsProp, lastArgsProps.current) && !skip) {
            getEmptyList();
            lastArgsProps.current = argsProp as Args;
        }
    }, [argsProp, lastArgsProps, skip]);

    const getMore = async () => {
        if (isLoadingRef.current || isDisabledMoreRef.current || skip) {
            return;
        }

        const requestParams = {
            ...argsProp,
            ...getPaginationParams(data[data.length - 1]),
        };

        if (!isEqual(requestParams, lastRequestParams.current)) {
            try {
                isLoadingRef.current = true;

                const result = await getDataRequest(requestParams as Args);

                let listResponse: ListResponse<DataItem>;

                if ('data' in result) {
                    listResponse = result.data;
                    setTotalCount(result.total_count);
                } else {
                    listResponse = result;
                    setTotalCount();
                }

                if (listResponse.length > 0) {
                    setData((prev) => [...prev, ...listResponse]);
                } else {
                    isDisabledMoreRef.current = true;
                }
            } catch (e) {
                console.log(e);
            }

            setTimeout(() => {
                isLoadingRef.current = false;
            }, 50);
        }
    };

    useLayoutEffect(() => {
        const element = scrollElement.current;

        if (isLoadingRef.current || !data.length) return;

        if (element.scrollHeight - element.clientHeight <= 0) {
            getMore().catch(console.log);
        }
    }, [data]);

    const onScroll = useCallback(() => {
        if (isDisabledMoreRef.current || isLoadingRef.current) {
            return;
        }

        const element = scrollElement.current;

        const scrollPositionFromBottom = element.scrollHeight - (element.clientHeight + element.scrollTop);

        if (scrollPositionFromBottom < SCROLL_POSITION_GAP) {
            getMore().catch(console.log);
        }
    }, [getMore]);

    useEffect(() => {
        document.addEventListener('scroll', onScroll);

        return () => {
            document.removeEventListener('scroll', onScroll);
        };
    }, [onScroll]);

    const isLoadingMore = data.length > 0 && isFetching;

    return {
        data,
        totalCount,
        isLoading: isLoading || (data.length === 0 && isFetching),
        isLoadingMore,
        refreshList: getEmptyList,
    } as const;
};
